import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import styles from './SwipeCard.module.css';

type Direction = 'right' | 'left';
type Phase = 'idle' | 'dragging' | 'confirming' | 'exit-right' | 'exit-left';
type Controls = { right: () => void; left: () => void; busy: boolean };
export type CardPreview = { title: string; caption: string };

/** A deliberate swipe has the same approval semantics as its visible button. */
export function SwipeCard({ children, onRight, onLeft, rightLabel = 'Send', leftLabel = 'Dismiss',
  previews = [], itemKey = '', rightConfirmation, entryDirection = 'right', rightUndoable = true, rightTone = 'green',
}: {
  children: ReactNode | ((controls: Controls) => ReactNode);
  onRight?: (() => void) | undefined; onLeft?: (() => void) | undefined;
  rightLabel?: string; leftLabel?: string; previews?: CardPreview[];
  itemKey?: string; rightConfirmation?: ReactNode; entryDirection?: Direction;
  rightUndoable?: boolean; rightTone?: 'green' | 'blue';
}) {
  const card = useRef<HTMLDivElement>(null);
  const origin = useRef<{ x: number; y: number; width: number; pointer: number }>();
  const distance = useRef(0);
  const locked = useRef(false);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const [offset, setOffset] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const [travel, setTravel] = useState(900);
  const [countdown, setCountdown] = useState(4);
  const [pendingDirection, setPendingDirection] = useState<Direction>('right');
  const callbacks = useRef({ onRight, onLeft });
  callbacks.current = { onRight, onLeft };
  const threshold = (width: number) => Math.max(96, width * .32);
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  useLayoutEffect(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    locked.current = false;
    origin.current = undefined;
    distance.current = 0;
    setOffset(0);
    setPhase('idle');
    return () => { timers.current.forEach(clearTimeout); };
  }, [itemKey]);
  const later = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };
  const reset = () => { origin.current = undefined; distance.current = 0; setOffset(0); setPhase('idle'); };
  const act = (direction: Direction) => {
    const callback = direction === 'right' ? onRight : onLeft;
    if (!callback || locked.current) return;
    locked.current = true;
    origin.current = undefined;
    setPendingDirection(direction);
    setCountdown(4);
    setTravel((card.current?.clientWidth ?? 700) + 120);
    setPhase(`exit-${direction}`);
    later(() => {
      if (direction === 'right' && !rightUndoable) {
        const current = callbacks.current.onRight;
        reset();
        current?.();
        locked.current = false;
      } else setPhase('confirming');
    }, reducedMotion() ? 0 : 380);
  };
  const undo = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    locked.current = false;
    reset();
  };
  useEffect(() => {
    if (phase !== 'confirming') return;
    const deadline = Date.now() + 4000;
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining > 0) return;
      clearInterval(timer);
      const callback = pendingDirection === 'right' ? callbacks.current.onRight : callbacks.current.onLeft;
      if (!callback) { locked.current = false; reset(); return; }
      // The undo window expires before any external action is submitted.
      reset();
      callback();
      locked.current = false;
    }, 100);
    return () => clearInterval(timer);
  }, [phase, pendingDirection, itemKey]);
  const progress = Math.min(1, Math.abs(offset) / threshold(card.current?.clientWidth ?? 700));
  const busy = phase === 'confirming' || phase.startsWith('exit');
  return <div className={styles.viewport}>
    <div className={styles.stack} data-phase={phase} data-depth={Math.min(previews.length, 2)} data-right-tone={rightTone}
      data-direction={phase === 'confirming' || phase.startsWith('exit') ? pendingDirection : offset >= 0 ? 'right' : 'left'}
      style={{ '--drag': `${offset}px`, '--tilt': `${offset / 65}deg`, '--progress': progress,
        '--travel': `${travel}px` } as CSSProperties}>
      {previews.slice(0, 2).map((preview, index) => <div key={index} className={styles.preview} data-level={index + 1} aria-hidden="true">
        <div className={styles.previewTab}><span className={styles.previewDot} /><strong>{preview.title}</strong><span>{preview.caption}</span></div>
      </div>)}
      <div key={itemKey} className={styles.entry} data-entry={entryDirection}>
        <div ref={card} className={styles.card}
          onPointerDown={event => {
            if (locked.current || event.button !== 0 || event.isPrimary === false || event.clientX < 24 ||
              (event.target as HTMLElement).closest('input,textarea,button,a,select,summary,[contenteditable],label')) return;
            origin.current = { x: event.clientX, y: event.clientY, width: event.currentTarget.clientWidth, pointer: event.pointerId };
          }}
          onPointerMove={event => {
            const start = origin.current;
            if (!start || event.pointerId !== start.pointer || locked.current) return;
            const dx = event.clientX - start.x, dy = event.clientY - start.y;
            if (phase !== 'dragging' && Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { reset(); return; }
            if (phase !== 'dragging' && (Math.abs(dx) < 10 || Math.abs(dx) < Math.abs(dy) * 1.4)) return;
            event.currentTarget.setPointerCapture(event.pointerId);
            distance.current = dx;
            setPhase('dragging');
            const allowed = dx > 0 ? onRight : onLeft;
            setOffset(allowed ? Math.sign(dx) * Math.min(Math.abs(dx), start.width * .8) : dx * .12);
          }}
          onPointerUp={event => {
            const start = origin.current;
            if (!start || event.pointerId !== start.pointer) return;
            const dx = distance.current;
            const requiredDistance = threshold(start.width);
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            if (Math.abs(dx) >= requiredDistance && (dx > 0 ? onRight : onLeft)) act(dx > 0 ? 'right' : 'left');
            else reset();
          }}
          onPointerCancel={() => { if (!locked.current) reset(); }}
          onLostPointerCapture={() => { if (!locked.current) reset(); }}>
          <div className={styles.content} ref={element => {
            if (busy) element?.setAttribute('inert', ''); else element?.removeAttribute('inert');
          }}>
            {typeof children === 'function' ? children({ right: () => act('right'), left: () => act('left'), busy }) : children}
          </div>
          <div className={styles.tint} aria-hidden="true" />
          {((phase === 'dragging' && progress >= 1) || phase.startsWith('exit')) && <span className={styles.stamp} aria-hidden="true">{(phase.startsWith('exit') ? pendingDirection === 'right' : offset >= 0) ? rightLabel : leftLabel}</span>}
        </div>
      </div>
      {phase === 'confirming' && <div className={styles.confirmation}>
        <div role="status" aria-live="polite">
          <strong>{pendingDirection === 'right' ? rightLabel : leftLabel} in {countdown}s</strong>
        </div>
        {pendingDirection === 'right' && rightConfirmation}
        <button type="button" className={styles.undo} onClick={undo} autoFocus>Undo</button>
      </div>}
    </div>
  </div>;
}
